/**
 * Gandiv — the rule. Zero imports, byte-identical backend twin
 * (backend/src/services/strategies/gandivEngine.ts, `scripts/check-engine-parity.mjs`).
 *
 * Inputs are plain arrays, index-aligned: the SOURCE close the rule is judged
 * on (Reg / HA / CLH / CLN body), the REAL close every price is taken at, and
 * the band as it was known at each bar (utils/gandivBand.ts). Every bar passed
 * here has CLOSED.
 *
 * THE RULE (agreed with the owner 2026-09-24)
 *   cross up   src[t] > upper[t] and src[t-1] <= upper[t-1]   (each bar vs its own band)
 *   cross down src[t] < lower[t] and src[t-1] >= lower[t-1]
 *
 *   FLAT      cross up -> LONG, SL = lower[t];  cross down -> SHORT, SL = upper[t]
 *   LONG      src[t] < SL -> STOP AND REVERSE to SHORT; the short's SL is the
 *             UPPER line at the moment the stopped long was entered
 *             otherwise cross down -> reverse to SHORT with a fresh SL = upper[t]
 *             a cross up is ignored: the SL is fixed and never trails
 *   SHORT     the mirror image
 *
 * Every trade records the band at its OWN entry bar, so a chain recurses: a
 * long stopped -> short SL = that long's entry upper; that short stopped -> long
 * SL = the short's entry lower (the band at the reversal bar).
 *
 * When the stop and an opposite band cross land on the same bar, the stop wins:
 * it is the literal rule, the band reversal is the extension.
 *
 * WRONG-SIDE GUARD. A chained SL can already be on the wrong side of the price
 * at the reversal (the band has moved a long way since). Such a stop would fire
 * on the next bar for no reason, so the band's opposite line at the reversal bar
 * is used instead; if even that is on the wrong side the position goes FLAT and
 * waits for a band cross. Both are counted, never silent.
 *
 * PRICES ARE REAL. Entries and reversals are at the REAL close of the bar that
 * decided them (close-confirmed, so never at the level). SL values are band
 * prices, which are real.
 */

export type GandivSide = 'LONG' | 'SHORT';
export type GandivPos = 'LONG' | 'SHORT' | 'FLAT';
/** How a trade was opened. */
export type GandivHow = 'BAND' | 'SL_REV' | 'BAND_REV';
/** Where its stop came from. */
export type GandivSlFrom = 'BAND' | 'CHAIN' | 'FALLBACK';

export interface GandivTrade {
  side: GandivSide;
  how: GandivHow;
  slFrom: GandivSlFrom;
  entryIdx: number;
  entryTime: number;
  entryPrice: number;
  /** The band at the entry bar — the chain's memory. */
  entryUpper: number | null;
  entryLower: number | null;
  sl: number;
  exitIdx: number | null;
  exitTime: number | null;
  exitPrice: number | null;
  why: 'SL' | 'BAND' | null;
}

export interface GandivStep {
  time: number;
  /** Position AFTER this bar's close. */
  pos: GandivPos;
  /** The stop in force after this bar, null when flat. */
  sl: number | null;
  /** What happened on this bar. */
  event: 'BAND' | 'SL_REV' | 'BAND_REV' | 'SL_FLAT' | null;
}

export interface GandivResult {
  steps: GandivStep[];
  trades: GandivTrade[];
  /** Chained SLs replaced by the band line at the reversal bar. */
  fallbacks: number;
  /** Reversals that went FLAT because no stop was on the right side. */
  flats: number;
}

export function computeGandivTrades(
  times: number[],
  src: number[],
  real: number[],
  upper: Array<number | null>,
  lower: Array<number | null>,
  startIdx: number,
): GandivResult {
  const n = times.length;
  const steps: GandivStep[] = [];
  const trades: GandivTrade[] = [];
  let fallbacks = 0;
  let flats = 0;
  let cur: GandivTrade | null = null;

  const ok = (v: number | null | undefined): boolean => v != null && isFinite(v);
  const open = (i: number, side: GandivSide, how: GandivHow, sl: number, slFrom: GandivSlFrom): GandivTrade => {
    const t: GandivTrade = {
      side, how, slFrom,
      entryIdx: i, entryTime: times[i], entryPrice: real[i],
      entryUpper: ok(upper[i]) ? (upper[i] as number) : null,
      entryLower: ok(lower[i]) ? (lower[i] as number) : null,
      sl,
      exitIdx: null, exitTime: null, exitPrice: null, why: null,
    };
    trades.push(t);
    return t;
  };
  const close = (t: GandivTrade, i: number, why: 'SL' | 'BAND'): void => {
    t.exitIdx = i; t.exitTime = times[i]; t.exitPrice = real[i]; t.why = why;
  };

  for (let i = 0; i < n; i++) {
    let event: GandivStep['event'] = null;
    const canCross = i >= Math.max(1, startIdx)
      && ok(upper[i]) && ok(lower[i]) && ok(upper[i - 1]) && ok(lower[i - 1]);
    const crossUp = canCross && src[i] > (upper[i] as number) && src[i - 1] <= (upper[i - 1] as number);
    const crossDn = canCross && src[i] < (lower[i] as number) && src[i - 1] >= (lower[i - 1] as number);

    if (cur && i > cur.entryIdx) {
      const long: boolean = cur.side === 'LONG';
      const hit = long ? src[i] < cur.sl : src[i] > cur.sl;
      if (hit) {
        close(cur, i, 'SL');
        const side: GandivSide = long ? 'SHORT' : 'LONG';
        // The stopped trade's entry band, opposite line.
        const chained = long ? cur.entryUpper : cur.entryLower;
        const here = long ? upper[i] : lower[i];
        const right = (v: number | null | undefined): boolean =>
          ok(v) && (side === 'SHORT' ? (v as number) > src[i] : (v as number) < src[i]);
        if (right(chained)) {
          cur = open(i, side, 'SL_REV', chained as number, 'CHAIN');
          event = 'SL_REV';
        } else if (right(here)) {
          fallbacks++;
          cur = open(i, side, 'SL_REV', here as number, 'FALLBACK');
          event = 'SL_REV';
        } else {
          flats++;
          cur = null;
          event = 'SL_FLAT';
        }
      } else if (long ? crossDn : crossUp) {
        close(cur, i, 'BAND');
        cur = long
          ? open(i, 'SHORT', 'BAND_REV', upper[i] as number, 'BAND')
          : open(i, 'LONG', 'BAND_REV', lower[i] as number, 'BAND');
        event = 'BAND_REV';
      }
    } else if (!cur) {
      if (crossUp) { cur = open(i, 'LONG', 'BAND', lower[i] as number, 'BAND'); event = 'BAND'; }
      else if (crossDn) { cur = open(i, 'SHORT', 'BAND', upper[i] as number, 'BAND'); event = 'BAND'; }
    }

    steps.push({ time: times[i], pos: cur ? cur.side : 'FLAT', sl: cur ? cur.sl : null, event });
  }
  return { steps, trades, fallbacks, flats };
}
